import { Link } from 'react-router-dom'
import { APP_NAME, PLAN_LIMITS } from '@/lib/config'
import { useEffect } from 'react'

export default function ForShopify() {
  useEffect(() => {
    document.title = `Shopify Analytics - ${APP_NAME} | Privacy-First Store Tracking`
  }, [])

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo-icon.png" alt={APP_NAME} className="w-8 h-8" />
              <span className="text-xl font-bold text-gray-900">{APP_NAME}</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/login" className="text-sm font-medium text-gray-600 hover:text-gray-900">
                Sign in
              </Link>
              <Link to="/signup" className="btn-primary">
                Start free trial
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-green-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-4 py-1 rounded-full text-sm font-medium mb-6">
            <ShopifyIcon className="w-4 h-4" />
            Built for Shopify
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            Privacy-First Analytics for Your Shopify Store
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Understand your customers without compromising their privacy.
            Auto-detects Shopify, one-line install, no app required.
          </p>
          <Link to="/signup" className="btn-primary py-3 px-8 text-base">
            Start tracking free
          </Link>
          <p className="mt-4 text-sm text-gray-500">
            No app installation needed. Works with any Shopify theme.
          </p>
        </div>
      </section>

      {/* Why Shopify */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">
            Built for E-commerce
          </h2>
          <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
            {APP_NAME} understands your Shopify store and provides insights that matter for selling online.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature) => (
              <div key={feature.title} className="card">
                <div className="text-3xl mb-4">{feature.emoji}</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Installation Guide */}
      <section className="py-16 bg-gray-50 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">
            Install in 2 Minutes
          </h2>
          <p className="text-gray-600 text-center mb-12">
            No Shopify app needed. Just add one line of code to your theme.
          </p>

          <div className="space-y-6">
            {installSteps.map((step, i) => (
              <div key={i} className="flex gap-4">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-green-700 font-semibold">{i + 1}</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{step.title}</h3>
                  <p className="text-gray-600">{step.description}</p>
                  {step.image && (
                    <div className="mt-3 bg-gray-100 rounded-lg p-4 text-sm text-gray-500 italic">
                      {step.image}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 bg-gray-900 rounded-lg p-6">
            <div className="flex justify-between items-center mb-2">
              <p className="text-gray-400 text-sm">Your tracking script</p>
              <span className="text-xs text-gray-500">Paste before &lt;/head&gt;</span>
            </div>
            <code className="text-green-400 text-sm break-all">
              {`<script async src="https://userinsight.live/tracker.js" data-site-key="YOUR_KEY"></script>`}
            </code>
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">
            vs Other Shopify Analytics
          </h2>
          <p className="text-gray-600 text-center mb-12">
            Compare {APP_NAME} to built-in Shopify Analytics and other third-party tools.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-4 px-4 text-gray-600 font-medium">Feature</th>
                  <th className="text-center py-4 px-4 text-primary-600 font-semibold">{APP_NAME}</th>
                  <th className="text-center py-4 px-4 text-gray-600 font-medium">Shopify Analytics</th>
                  <th className="text-center py-4 px-4 text-gray-600 font-medium">Google Analytics</th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((row, i) => (
                  <tr key={i} className="border-b border-gray-100">
                    <td className="py-4 px-4 text-gray-900 font-medium">{row.feature}</td>
                    <td className="py-4 px-4 text-center">
                      {typeof row.userinsight === 'boolean' ? (
                        row.userinsight ? (
                          <CheckIcon className="w-5 h-5 text-green-500 mx-auto" />
                        ) : (
                          <XIcon className="w-5 h-5 text-red-500 mx-auto" />
                        )
                      ) : (
                        <span className="text-gray-900">{row.userinsight}</span>
                      )}
                    </td>
                    <td className="py-4 px-4 text-center">
                      {typeof row.shopify === 'boolean' ? (
                        row.shopify ? (
                          <CheckIcon className="w-5 h-5 text-green-500 mx-auto" />
                        ) : (
                          <XIcon className="w-5 h-5 text-red-500 mx-auto" />
                        )
                      ) : (
                        <span className="text-gray-500">{row.shopify}</span>
                      )}
                    </td>
                    <td className="py-4 px-4 text-center">
                      {typeof row.ga === 'boolean' ? (
                        row.ga ? (
                          <CheckIcon className="w-5 h-5 text-green-500 mx-auto" />
                        ) : (
                          <XIcon className="w-5 h-5 text-red-500 mx-auto" />
                        )
                      ) : (
                        <span className="text-gray-500">{row.ga}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-gray-50 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Frequently Asked Questions
          </h2>
          <div className="space-y-8">
            {faqs.map((faq, i) => (
              <div key={i} className="border-b border-gray-200 pb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{faq.question}</h3>
                <p className="text-gray-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-green-600 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to understand your Shopify visitors?
          </h2>
          <p className="text-green-100 mb-8">
            Start free with {PLAN_LIMITS.sessionsPerMonth.toLocaleString()} sessions/month. No credit card required.
          </p>
          <Link
            to="/signup"
            className="inline-flex items-center justify-center px-8 py-3 text-base font-medium rounded-lg bg-white text-green-600 hover:bg-green-50 transition-colors"
          >
            Start free trial
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-gray-200 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} {APP_NAME}. All rights reserved.
            </p>
            <div className="flex gap-6">
              <Link to="/" className="text-gray-500 hover:text-gray-700 text-sm">Home</Link>
              <Link to="/vs-google-analytics" className="text-gray-500 hover:text-gray-700 text-sm">vs Google Analytics</Link>
              <Link to="/for-wordpress" className="text-gray-500 hover:text-gray-700 text-sm">WordPress Analytics</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}

function ShopifyIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M15.337 23.979l7.216-1.561s-2.604-17.613-2.625-17.756a.465.465 0 00-.377-.403c-.163-.038-3.347-.494-3.347-.494l-2.24-2.24c-.218-.199-.64-.14-.803-.093l-1.161.36-.614-1.851c-.279-.841-1.256-1.256-2.097-.977L4.689.001c-1.014-.27-2.096.38-2.38 1.428L.001 10.063c-.249.908.35 1.857 1.31 2.083l1.097.257v10.65a.45.45 0 00.464.464l12.465-.538zm-5.178-12.6l1.065-3.702 3.144.685c0 .006-1.139 3.017-4.21 3.017h.001z"/>
    </svg>
  )
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  )
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  )
}

const features = [
  {
    emoji: '🔍',
    title: 'Session Replay',
    description: 'Watch how customers browse your store. See where they get stuck, what they click, and why they abandon carts.',
  },
  {
    emoji: '🔒',
    title: 'Privacy-First',
    description: 'No personal data collection. First-party cookies only. GDPR compliant without consent banners in most cases.',
  },
  {
    emoji: '⚡',
    title: 'Lightweight',
    description: 'Under 10KB script won\'t slow down your store. Zero impact on page load and Core Web Vitals.',
  },
  {
    emoji: '📊',
    title: 'Real-Time Dashboard',
    description: 'See visitors as they browse. Track traffic sources, popular products, and customer journeys live.',
  },
  {
    emoji: '🎯',
    title: 'Auto-Detection',
    description: 'Automatically detects Shopify and optimizes tracking. No configuration needed.',
  },
  {
    emoji: '💳',
    title: 'No App Required',
    description: 'Install with one script tag. No Shopify app to manage, no monthly app fees.',
  },
]

const installSteps = [
  {
    title: 'Sign up for UserInsight.live',
    description: 'Create your free account and add your Shopify store domain.',
  },
  {
    title: 'Go to your Shopify Admin',
    description: 'Navigate to Online Store → Themes in your Shopify admin panel.',
  },
  {
    title: 'Edit your theme code',
    description: 'Click the "..." menu on your current theme, then select "Edit code".',
    image: 'Actions → Edit code',
  },
  {
    title: 'Open theme.liquid',
    description: 'In the Layout folder, click on "theme.liquid" to open it.',
  },
  {
    title: 'Paste the script',
    description: 'Find the </head> tag and paste your tracking script just before it. Save the file.',
  },
  {
    title: 'Verify installation',
    description: 'Visit your store in a new tab, then check your UserInsight.live dashboard for the first pageview.',
  },
]

const comparisonData = [
  { feature: 'Session replay', userinsight: true, shopify: false, ga: false },
  { feature: 'Privacy-first', userinsight: true, shopify: true, ga: false },
  { feature: 'No consent banner needed', userinsight: true, shopify: true, ga: false },
  { feature: 'Real-time visitors', userinsight: true, shopify: false, ga: true },
  { feature: 'Traffic sources', userinsight: true, shopify: true, ga: true },
  { feature: 'Page analytics', userinsight: true, shopify: true, ga: true },
  { feature: 'Free tier', userinsight: '5K sessions', shopify: 'Included', ga: 'Yes' },
  { feature: 'Script size', userinsight: '<10KB', shopify: 'N/A', ga: '45KB+' },
  { feature: 'Setup time', userinsight: '2 min', shopify: 'Built-in', ga: '30+ min' },
]

const faqs = [
  {
    question: 'Do I need to install a Shopify app?',
    answer: 'No. UserInsight.live works with a simple script tag added to your theme. No app installation, no app fees, no app permissions.',
  },
  {
    question: 'Will this slow down my store?',
    answer: 'No. Our tracker is under 10KB and loads asynchronously. It has zero impact on your store\'s page load time and Core Web Vitals scores.',
  },
  {
    question: 'Can I use this alongside Shopify Analytics?',
    answer: 'Yes! UserInsight.live works alongside Shopify\'s built-in analytics. Get the best of both - Shopify for sales data, UserInsight for visitor behavior.',
  },
  {
    question: 'Does it work with Shopify Plus?',
    answer: 'Yes. UserInsight.live works with all Shopify plans including Shopify Plus. The installation process is the same.',
  },
  {
    question: 'Will it track checkout and thank you pages?',
    answer: 'The script tracks all pages where it\'s installed. For checkout pages on Shopify Plus, you can add the script to checkout.liquid as well.',
  },
  {
    question: 'Is it GDPR compliant for my EU customers?',
    answer: 'Yes. UserInsight.live uses first-party cookies only and collects no personal data. In most cases, you don\'t need a cookie consent banner for our analytics.',
  },
]
